// This file is part of Indico.UN.
// Copyright (C) 2019 - 2026 United Nations. All rights reserved.

import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {Form as FinalForm} from 'react-final-form';
import {Button, Form, Label, Modal, TextArea} from 'semantic-ui-react';

import {FinalField} from 'indico/react/forms';
import {Translate} from 'indico/react/i18n';
import {injectModal} from 'indico/react/util';

export default function RequestConfirmWithReason({
  header,
  children,
  onConfirm,
  confirmText,
  cancelText,
  onClose,
  size,
  negative,
}) {
  const [open, setOpen] = useState(true);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [reason, setReason] = useState(null);

  const onSubmit = async (values) => {
    setRequestInProgress(true);
    onConfirm(reason);
    setOpen(false);
  }

  return (
    <FinalForm
      onSubmit={onSubmit}
    >
      {({handleSubmit}) => (
        <Modal
          onClose={onClose}
          size={size}
          closeIcon={false}
          closeOnDimmerClick={!requestInProgress}
          closeOnEscape={!requestInProgress}
          open={open}
        >
          <Modal.Header>{header}</Modal.Header>
          <Modal.Content>
            {children}
            <Form onSubmit={handleSubmit}>
              <FinalField name="reason">
                {({input, meta}) => (
                  <Form.Field error={meta.error && meta.touched}>
                    <TextArea
                      {...input}
                      onChange={(e) => {
                        setReason(e.target.value.replaceAll('\n', ' ').trim());
                        input.onChange(e);
                      }}
                      style={{
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        minHeight: 40,
                        width: "100%"
                      }}
                    />
                  </Form.Field>
                )}
              </FinalField>
              <Button
                type="submit"
                loading={requestInProgress}
                negative={negative}
                primary
                disabled={(!reason || reason.length < 4 || reason.length > 128) || requestInProgress}>
                {confirmText}
              </Button>
              <Button
                type="button"
                onClick={onClose}
                disabled={requestInProgress}
              >
                {cancelText}
              </Button>
            </Form>
          </Modal.Content>
        </Modal>
      )}
    </FinalForm>
  );
}

RequestConfirmWithReason.propTypes = {
  header: PropTypes.node.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  size: PropTypes.oneOf(['mini', 'tiny', 'small', 'large', 'fullscreen']),
  negative: PropTypes.bool,
};

RequestConfirmWithReason.defaultProps = {
  children: null,
  confirmText: Translate.string('Ok'),
  cancelText: Translate.string('Cancel'),
  size: 'tiny',
  negative: false,
};

export function showRequestConfirmWithReason(header='', description='') {
  return injectModal(resolve => (
    <RequestConfirmWithReason
      header={header}
      onConfirm={reason => reason ? resolve({'reason': reason, 'cancel': false}) : resolve({
        'reason': null,
        'cancel': false
      })}
      onClose={() => resolve({'reason': null, 'cancel': true})}
    >
      {description}
    </RequestConfirmWithReason>
  ));
}
